import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAddress } from 'viem'

import { loadProfile, ProfileMetadata } from '@/components/Profile/metadata'
import {
  getDefaultBannerImage,
  getDefaultProfileImage,
} from '@/plugins/auth/AuthProvider/effects/functions'
import { StoredTheme } from '@/utils/utils'

import useL1NS from './useL1NS'
import useLocalStorage from './useLocalStorage'

const useProfileDetails = (creator: string) => {
  // Creator Profile Picture
  const { getL1nsProfile, getL1nsName } = useL1NS()
  const [userTheme] = useLocalStorage<StoredTheme>('userTheme')
  const defaultPfp = useMemo(() => getDefaultProfileImage(userTheme?.isLightMode), [userTheme])
  const defaultBanner = useMemo(() => getDefaultBannerImage(userTheme?.isLightMode), [userTheme])
  const [creatorPfp, setCreatorPfp] = useState<string>(defaultPfp)
  const [isLoadingProfileInfo, setIsLoadingProfileInfo] = useState<boolean>(false)
  const [isLoadingUsername, setIsLoadingUsername] = useState<boolean>(false)
  const [isError, setIsError] = useState<string>('')
  const [creatorName, setCreatorName] = useState<string>('No Name')
  const [creatorUsername, setCreatorUsername] = useState<string>('')
  const [creatorBanner, setCreatorBanner] = useState<string>(defaultBanner)
  const [profile, setProfile] = useState<ProfileMetadata>({
    name: 'No Name',
    description: '',
    image: '',
    creator: {
      name: '',
      link: '',
    },
    bannerImage: '',
  })

  const getCreatorDetails = useCallback(async () => {
    try {
      if (!isAddress(creator)) {
        return
      }
      setIsLoadingProfileInfo(true)
      // Get profile url from contract
      const profileUrl = await getL1nsProfile(creator)

      // Load profile from IPFS
      if (profileUrl) {
        const p = await loadProfile(profileUrl)
        setProfile(p || profile)
        if (p) {
          if (p.image) {
            setCreatorPfp(p.image)
          }
          if (p.bannerImage) {
            setCreatorBanner(p.bannerImage)
          }
          if (p.name) {
            setCreatorName(p.name)
          }
        }
      }
      setIsLoadingProfileInfo(false)
      setIsLoadingUsername(true)

      const username = await getL1nsName(creator)
      if (username) {
        setCreatorUsername(`@${username}`)
      } else {
        setCreatorUsername(creator)
      }
      setIsLoadingUsername(false)
    } catch (e: any) {
      setIsError(e.message)
      setIsLoadingProfileInfo(false)
      setIsLoadingUsername(false)
      setCreatorPfp(defaultPfp)
    }
  }, [creator])

  useEffect(() => {
    getCreatorDetails()
  }, [getCreatorDetails])

  return {
    isError,
    isLoadingProfileInfo,
    isLoadingUsername,
    creatorPfp,
    creatorName,
    creatorUsername,
    creatorBanner,
  }
}
export default useProfileDetails
